import { ApplicationRef, Injectable } from '@angular/core';
import { FormGroup } from "@angular/forms";
import { Router } from '@angular/router';
import { ModalController, ToastController } from '@ionic/angular';
import { cloneDeep, isNil } from 'lodash-es';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { first, map } from 'rxjs/operators';
import { ProductService } from 'src/app/shared-lib/domains/product';
import { UserService } from 'src/app/shared-lib/domains/user';
import { WalletService } from 'src/app/shared-lib/domains/wallet';
import { PostalAddress } from 'src/app/shared-lib/global/models';
import { environment } from 'src/environments/environment';
import { AppCoreService } from '../core/app-core.service';
import {
  Order,
} from '../models/models';
import { OrderRestartDialogComponent } from '../order/order-restart-dialog/order-restart-dialog.component';
import { ShippingType } from '../shared-lib/core/settings';
import { OrderService as DomainOrderService, OrderProcessStatus, OrderShippingStatus, OrderType } from '../shared-lib/domains/order';
import { OrderPaymentStatus, PaymentType } from '../shared-lib/domains/payment';
import { AppService } from './app.service';
import { CartService } from './cart.service';
import { EncryptionService } from "./encryption.service";
import { OrderService } from './order.service';
import { ShippingService } from "./services/shipping.service";

//Pendiente
//http://localhost:8100/cart?orderId=idx75pV3CD2rWtD5FpoE
//Fail
//http://localhost:8100/cart?orderId=NxTBTDDHi9VMCrnELzt2

@Injectable({
  providedIn: 'root'
})
export class OrderStateService {
  private readonly _loading = new BehaviorSubject<boolean>(false);
  private readonly _order = new BehaviorSubject<Order>(null);
  readonly order$ = this._order.asObservable();
  readonly headerMessage$: Observable<string> = this.order$.pipe(
    map(order => {
      let msg;

      if (order?.type === OrderType.COMPLEMENTARY && order?.linkedOrders?.length > 0) {
        msg = `Pedido complementario de #${order.linkedOrders[0].number}`;
      }
      return msg;
    })
  );
  private userSubscription: Subscription;

  _shippingAddress = null;
  constructor(
    private userService: UserService,
    private cartService: CartService,
    private shippingService: ShippingService,
    private walletService: WalletService,
    private orderService: OrderService,
    private appService: AppService,
    private appCoreService: AppCoreService,
    private appRef: ApplicationRef,
    private encryptS: EncryptionService,
    private domainOrderService: DomainOrderService,
    private toastController: ToastController,
    private modalController: ModalController,
    private router: Router,
    private productService: ProductService

  ) {
    this.appCoreService.isAppReady$.pipe(
      first(isAppReady => isAppReady)
    )
      .subscribe(isAppReady => {
        this._order.next(this.newOrder());
        this.initService();
      });

    this.order$.subscribe(order => {
      if (order && JSON.stringify(order.shippingAddress) !== JSON.stringify(this._shippingAddress)) {
        this._shippingAddress = order.shippingAddress;
        const newMustReduceTaxes = ['35', '38'].includes(order?.shippingAddress?.provinceId);
        if (newMustReduceTaxes !== this.cartService.mustReduceTax) {
          this.cartService.mustReduceTax = newMustReduceTaxes;
          this.productService.mustReduceTax = newMustReduceTaxes;
          this.cartService.refreshCartPrice(true);
        }
      }
    });
  }

  subscribeUser(witSubscription: boolean) {
    const isSubscribed = this.userSubscription && !this.userSubscription.closed;
    if (!witSubscription && isSubscribed) {
      this.userSubscription.unsubscribe();
    }
    if (witSubscription && !isSubscribed) {
      this.userSubscription = this.userService.user$.subscribe(user => this.setUserOrder(user));
    }
  }

  async initService() {
    const savedOrder = await this.getStorageOrder();
    if (savedOrder) {
      this.setOrder(savedOrder);
    }

    this.cartService.onCartRefreshed().subscribe((data: any) => {
      if (this.cartService.onlyWalletBonus) {
        this.updateProperty({ shippingType: this.shippingService.checkShippingTypeAvailability(ShippingType.ECONOMIC, this._order.value.shippingAddress, this.cartService).available && !this.cartService.addressIsCanaryOrBalearics() ? ShippingType.ECONOMIC : ShippingType.STANDARD });
      }
      if (this.cartService.freeShipping && this._order.value.shippingType === ShippingType.ECONOMIC) {
        this.updateProperty({ shippingType: ShippingType.STANDARD });
      }
      if (this.cartService.shippingProductPrice > 500 && (this._order.value.shippingType === 'standard_24' || this._order.value.shippingType === 'urgent')) {
        this.updateProperty({ shippingType: ShippingType.STANDARD });
      }

      if (data) {
        this.updateProperty({ paymentType: data.paymentType });
      }
    });
  }

  async setOrder(order: Order = null) {
    this._order.next(order || this.newOrder());
    if (!order && this.userService.userG?.id) {
      const user = await this.userService.user;
      this.setUserOrder(user);
    }
  }

  updateProperty(data, orderFormGroup: FormGroup = null) {

    // Para no cambiar el valor de "name" se separa del orderFormGroup cuando este existe
    // Queremos que "name" sea igual al nombre del usuario
    const { name, ...orderFormGroupWithoutName } = orderFormGroup?.value || {};
    const currentOrderData = !!orderFormGroup ? { ...this._order.value, ...orderFormGroupWithoutName } : { ...this._order.value };
    this._order.next({ ...currentOrderData, ...data });
  }

  async setUserOrder(user) {
    // console.log("######-> user", user);
    this.cartService.userCoupon = null;
    const order = this._order.value;

    const preserveId = order.type === OrderType.COMPLEMENTARY;

    let dataUser = {} as any;

    if (user && user.id) {

      // Si el usuario ha introducido una dirección de envío nueva en el carrito, se usa esa dirección (cartService.userAddresses) --> Proceso de carrito activo
      // Si no, se recupera la dirección de envío por defecto del user directamente --> El proceso del carrito va a empezar
      const cartShippingAddressIndex = this.cartService.defaultCartAddressIndexes?.shipping;
      const shippingAddress = !!this.cartService.order?.shippingAddress?.pickupPointCode ?
        this.cartService.order.shippingAddress : (
          this.cartService.userAddresses?.shippingAddresses?.[cartShippingAddressIndex] ??
          user.shippingAddresses?.[user.defaultShippingAddressIndex]
        );

      // Si el usuario ha introducido una dirección de facturación nueva en el carrito, se usa esa dirección (cartService.userAddresses) --> Proceso de carrito activo
      // Si no, se recupera la dirección de facturación por defecto del user directamente --> El proceso del carrito va a empezar
      const cartBillingAddressIndex = this.cartService.defaultCartAddressIndexes?.billing;
      const billingAddress =
        this.cartService.userAddresses?.billingAddresses?.[cartBillingAddressIndex] ??
        user.billingAddresses?.[user.defaultBillingAddressIndex];

      dataUser = {
        ads: !isNil(user.acceptance?.ads) ? user.acceptance.ads : null,
      };

      if (shippingAddress) {
        dataUser.shippingAddress = shippingAddress;
      }
      if (billingAddress) {
        dataUser.billingAddress = billingAddress;
      }

      if (user.id !== order.userId) {
        this.cartService.userCoupon = await this.userService.getUserCoupon(user.id);
        dataUser = {
          ...dataUser,
          userId: user.id,
          name: user.displayName,
          email: user.email,
          phone: user.phoneNumber,
          gender: user.gender ?? null,
          ocupation: user.ocupation ?? null
        };
      }

    } else {
      this.cartService.applyWallet(false);
    }
    this.updateProperty(dataUser);
    this.cartService.setCouponStatus();

  }

  async loadSavedOrder(order: Order) {
    this._loading.next(true);
    this.resetOrder();

    //TODO terminar de completar los casos
    this.cartService.cart = { ...order.cart };
    order.cart = null;

    // Strip del prefijo de programación didáctica de todos los comentarios al cargar.
    // Si el pedido pertenece a otro usuario, el comentario completo se limpia para no
    // mostrar texto privado de un tercero. checkSkipFitToPage() re-añadirá el prefijo
    // antes de pagar si el usuario tiene skipFitToPage activo.
    const PD_PREFIX = '//PROGRAMACIÓN DIDÁCTICA// ';
    const isDifferentUser = this.userService.userG?.id !== order.userId;
    this.cartService.cart.items?.forEach(item => {
      if (item.product?.printingGroup) {
        const comment = item.product.printingGroup.comment ?? '';
        const cleanComment = comment.startsWith(PD_PREFIX) ? comment.slice(PD_PREFIX.length) : comment;
        item.product.printingGroup.comment = isDifferentUser ? '' : cleanComment;
      }
    });

    //Seteamos a quien pertenece el fee
    if (order.userId) {
      this.appService.fee = {
        userId: order.userId,
        type: 'share'
      };
    }

    if ([OrderPaymentStatus.PAID, OrderPaymentStatus.REFUNDED].includes(order.paymentStatus)) {

      //si la orden está pagada se utilizará para recomprar lo mismo, es como compartir
      this.cartService.cart.discounts = [];
    } else {
      let {
        id,
        number,
        userId,
        name,
        email,
        phone,
        policy,
        ads,
        billingAddress,
        shippingAddress,
        type,
        linkedOrders,
        shippingType
      } = order;
      if (this.userService.userG?.id !== order.userId) {
        userId = this.userService.userG?.id;
        name = '';
        email = this.userService.userG?.email;
        phone = this.userService.userG?.phoneNumber;
        policy = null;
        ads = null;
      }
      // Reconstruir pointSelected a partir de la shippingAddress ANTES de updateProperty
      // para que cuando order$ emita, los suscriptores ya encuentren pointSelected asignado.
      if (shippingAddress?.pickupPointCode) {
        this.cartService.pointSelected = {
          code: shippingAddress.pickupPointCode,
          name: shippingAddress.pickupPointName,
          address: shippingAddress.addressLine1,
          postalCode: shippingAddress.postalCode,
          city: shippingAddress.city,
          province: shippingAddress.province,
          shippingCourier: shippingAddress.shippingCourier,
        };
      }

      this.updateProperty({
        id,
        number,
        userId,
        name,
        email,
        phone,
        billingAddress,
        shippingAddress,
        type,
        linkedOrders,
        shippingType
      });
    }
    this.cartService.setProductUpdates();
    const availableGroups = await this.cartService.checkPrintFiles();
    if (!availableGroups) {
      await this.cartService.deleteUnavailableGroups();
    }
    this._loading.next(false);
  }

  //TODO Unificar criterios entre loadSavedOrder y esta
  //3 puntos a verificar id, ordernumber, userId y estado del pago.

  //El reintentar PAgo no es correcto si no se guarda el id
  //Diferenciar si viene por url o directo ?
  //Si tiene id la orden y está pagada ? si es transferencia
  loadOrder(order: Order) {
    //console.log('////////////==> CASO B loadOrder');
    // //console.log(order);
    this.resetOrder();
    this.cartService.cart = { ...order.cart };
    //TODO verificar si se debe colocar order.cart en null inclusive hacer una copia (esta es la orden guardada como tal)

    // Strip del prefijo de programación didáctica. checkSkipFitToPage() lo re-añadirá antes de pagar si procede.
    const PD_PREFIX = '//PROGRAMACIÓN DIDÁCTICA// ';
    this.cartService.cart.items?.forEach(item => {
      if (item.product?.printingGroup) {
        const comment = item.product.printingGroup.comment ?? '';
        item.product.printingGroup.comment = comment.startsWith(PD_PREFIX) ? comment.slice(PD_PREFIX.length) : comment;
      }
    });
    // order.cart = null;

    //El asunto radica en cuales datos reutilizamos
    //creo qu epodemos establecer una regla generica
    //si está guardado o logueado el userId es del logueado
    let { name, userId, email, phone, policy, ads } = order;
    const data = { userId, name, email, policy, ads, phone };

    if (this.userService.userG.id) {
      for (const property in data) {
        if (property === 'phone') {
          data.phone = this.userService.userG.phoneNumber ?? data.phone;
        } else {
          data[property] = this.userService.userG[property] ?? data[property];
        }
      }
    }

    this.updateProperty(data);
  }

  async saveDraft(): Promise<any> {
    const { shippingAddress, billingAddress, shippingType, ...order } = this._order.value;

    order.cart = this.cartService.cart;
    const canProceed = await this.orderService.optimizeAndCheckOrderSize(order);
    if (!canProceed) {
      return Promise.reject(new Error('Order size exceeds limit'));
    }
    order.id = null;
    order.number = null;
    order.paymentStatus = OrderPaymentStatus.PENDING;
    order.processStatus = OrderProcessStatus.PENDING;
    order.shippingStatus = OrderShippingStatus.PENDING;
    order.paymentType = null; //PaymentType.CREDITCARD;
    order.paymentService = null;
    const { id, ...draftOrder } = order as any;
    return this.userService.createUserDraftOrder(draftOrder.userId, draftOrder);
  }

  resetOrder() {
    this.removeStorageOrder();
    this.cartService.pointSelected = null;
    this.setOrder();
    this.cartService.resetCart();

    if (this.cartService.defaultCartAddressIndexes) {
      this.cartService.defaultCartAddressIndexes.shipping = cloneDeep(this.userService.userG.defaultShippingAddressIndex);
      this.cartService.defaultCartAddressIndexes.billing = cloneDeep(this.userService.userG.defaultBillingAddressIndex);
    }

  }

  async resetOrderAsync() {
    this.removeStorageOrder();
    await this.setOrder();
    this.cartService.resetCart();
  }

  newOrder(): Order {
    return {
      _version: 1,
      _appVersion: environment.version,
      id: null,
      userId: null,
      number: null,
      name: '',
      email: '',
      phone: '',
      cart: null,
      paymentStatus: OrderPaymentStatus.PENDING,
      processStatus: OrderProcessStatus.PENDING,
      // productionStatus: OrderProductionStatus.PENDING,
      shippingStatus: OrderShippingStatus.PENDING,
      paymentType: PaymentType.CREDITCARD, //null
      paymentService: null,
      shippingType: null,
      billingAddress: this.newAddress(),
      shippingAddress: this.newAddress(),
      // test undefined, last null
      policy: null,
      ads: null,
      type: OrderType.REGULAR,
      linkedOrders: null,
    };
  }

  newAddress(): PostalAddress {
    return {
      recipient: '',
      street: '',
      number: '',
      floor: '',
      addressLine1: '',
      addressLine2: '',
      city: '',
      province: '',
      provinceId: null,
      postalCode: '',
      country: '',
      phone: '',
      email: '',
      organization: '',
      taxId: '',
      dni: '',
    };
  }

  setStorageOrder(_order) {
    const order = { ..._order };
    delete order.cart;
    //TODO optimizar el guardado y ver bien cuales propiedades se debe guardar
    localStorage.setItem('order', this.encryptS.encrypt(JSON.stringify(order)));
  }

  async getStorageOrder() {
    try {
      let order = JSON.parse(this.encryptS.decrypt(localStorage.getItem('order')));
      if (order?.id) {
        const savedOrder = await this.orderService.get(order.id);
        if ([OrderPaymentStatus.REFUNDED, OrderPaymentStatus.PAID].includes(savedOrder?.paymentStatus)) {
          this.resetOrder();
          order = null;
        }
      }

      return order;
    } catch (e) {
      localStorage.removeItem('order');
      return;
    }
  }

  removeStorageOrder() {
    localStorage.removeItem('order');
  }

  getPostalCode() {
    return this._order.value.shippingAddress.postalCode;
  }

  onRestartOrder(order) {
    //show modal confirmation
    this.modalController.create({
      component: OrderRestartDialogComponent,
      componentProps: {
        order
      },
      cssClass: 'auto-height',
    }).then(modal => {
      modal.onDidDismiss().then(res => {
        if (res.data) {
          this.restartOrder(order);
        }
      });
      modal.present();
    });
  }

  restartOrder(order) {
    this.domainOrderService.restartOrder({ orderId: order.id }).then(() => {
      this.loadOrder(order);
      this.router.navigate(['/cart']);
      this.cartService.refreshCartPrice();
      setTimeout(() => {
        this.walletService.applyWallet$.next(true);
      }, 500);
      this.toastController.create({
        message: `El pedido ${order.number} ha sido recuperado correctamente`,
        color: 'success',
        duration: 4000
      }).then(t => t.present());
    }).catch(err => {
      this.toastController.create({
        message: 'Error al rehacer el pedido',
        color: 'danger',
        duration: 2000
      }).then(t => t.present());
    });
  }
}
